import React, { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { object, string, array, boolean } from 'yup';
import { Controller } from 'react-hook-form';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import InputMC from '../components/InputMC';
import DropdownMC from '../components/DropdownMC';
import { CUSTOM_FIELD_KINDS } from '../../constants/AppConstants';
import ButtonMC from '../components/ButtonMC';
import FormProviderMC from '../components/FormProviderMC';
import CustomFieldsModelMC from '../models/CustomFieldsModelMC';
import fileUtils from '../../util/fileUtils';
import { uuid } from '../../util/utils';

const CreateEditCustomField = ({
  fieldSelected,
  VARIANT = 'CREATE',
  onSuccess
}) => {
  const { t } = useTranslation('CustomFieldsMC');
  const dispatch = useDispatch();
  const isCreateVariant = VARIANT === 'CREATE';
  const title = isCreateVariant
    ? t('sidebar.titleCreate')
    : t('sidebar.titleEdit');

  const schema = object().shape({
    label: string().required(t('sidebar.validation.labelRequired')),
    name: string().required(t('sidebar.validation.identifierRequired')),
    kind: string().required(t('sidebar.validation.kindRequired')),
    mandatory_in_mercately: boolean(),
    show_in_catalog: boolean(),
    mandatory_in_shops: boolean(),
    list_options: array().when('kind', {
      is: 'list',
      then: () => array()
        .of(
          object().shape({
            label: string().required(t('sidebar.validation.labelRequired')),
            value: string().required(t('sidebar.validation.labelRequired'))
          })
        )
        .min(1, t('sidebar.validation.listMinOptions'))
        .test(
          'unique-keys',
          function validateUniqueKeys(listOptions) {
            if (!listOptions) {
              return true;
            }
            const labels = listOptions.map((option) => option.label);
            const values = listOptions.map((option) => option.value);
            const uniqueLabels = new Set(labels);
            const uniqueValues = new Set(values);
            if (uniqueLabels.size === labels.length && uniqueValues.size === values.length) {
              return true;
            }
            return this.createError({
              path: `${this.path}.root`,
              message: t('sidebar.validation.listUniqueOptions')
            });
          }
        ),
      otherwise: () => array().nullable()
    })
  });

  // Convertir list_options a array si viene como objeto
  const parseListOptions = (options) => {
    if (!options) return [{ label: '', value: '', isNew: true }];
    // Si es un objeto con índices numéricos, convertir a array
    const optionsArray = Array.isArray(options) ? options : Object.values(options);
    if (optionsArray.length === 0) return [{ label: '', value: '', isNew: true }];
    return optionsArray.map((opt) => ({
      id: opt.id,
      label: opt.label,
      value: opt.value,
      isNew: false
    }));
  };

  const defaultValues = {
    label: fieldSelected?.label || '',
    name: fieldSelected?.name || '',
    kind: fieldSelected?.kind || 'string',
    mandatory_in_mercately: fieldSelected?.mandatory_in_mercately || false,
    show_in_catalog: fieldSelected?.show_in_catalog || false,
    mandatory_in_shops: fieldSelected?.mandatory_in_shops || false,
    list_options: parseListOptions(fieldSelected?.list_options)
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    setError
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'list_options'
  });

  // Función para convertir label a identificador (slug)
  const labelToIdentifier = (label) => {
    if (!label) return '';
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9\s_]/g, '') // Solo letras, números, espacios y guiones bajos
      .replace(/\s+/g, '_') // Espacios a guiones bajos
      .replace(/_+/g, '_') // Múltiples guiones bajos a uno solo
      .replace(/^_|_$/g, ''); // Remover guiones bajos al inicio y final
  };

  // Watch para auto-generar identificador desde la etiqueta (solo en creación)
  const labelValue = watch('label');

  useEffect(() => {
    if (isCreateVariant && labelValue) {
      setValue('name', labelToIdentifier(labelValue));
    }
  }, [labelValue, isCreateVariant, setValue]);

  useEffect(() => {
    if (!isCreateVariant && fieldSelected) {
      setValue('label', fieldSelected.label);
      setValue('name', fieldSelected.name);
      setValue('kind', fieldSelected.kind);
      setValue('mandatory_in_mercately', fieldSelected.mandatory_in_mercately);
      setValue('show_in_catalog', fieldSelected.show_in_catalog);
      setValue('mandatory_in_shops', fieldSelected.mandatory_in_shops);
      if (fieldSelected.list_options) {
        setValue('list_options', parseListOptions(fieldSelected.list_options));
      }
    }
  }, [isCreateVariant, fieldSelected, setValue]);

  const buildDTO = (formData) => ({
    label: formData.label,
    name: formData.name,
    kind: formData.kind,
    mandatory_in_mercately: formData.mandatory_in_mercately,
    show_in_catalog: formData.show_in_catalog,
    mandatory_in_shops: formData.mandatory_in_shops,
    made_for: 'order',
    list_options: formData.kind === 'list'
      ? formData.list_options
        .filter((option) => option.label && option.value)
        .map((option) => ({
          id: option.id || uuid(),
          label: option.label,
          value: option.value
        }))
      : []
  });

  const onSubmit = async () => {
    const data = getValues();
    const dto = buildDTO(data);

    if (isCreateVariant) {
      try {
        await CustomFieldsModelMC.create(dto);
        showtoast(t('sidebar.successCreate'));
        dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        if (onSuccess) onSuccess();
      } catch (err) {
        if (err.message?.label) {
          err.message.label.forEach((errorMessage) => {
            setError('label', { type: 'server', message: errorMessage });
          });
        } else {
          setError('root', { type: 'server', message: t('sidebar.errorCreate') });
        }
      }
    } else {
      try {
        await CustomFieldsModelMC.update(fieldSelected.id, dto);
        showtoast(t('sidebar.successEdit'));
        dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        if (onSuccess) onSuccess();
      } catch (err) {
        if (err.message?.label) {
          err.message.label.forEach((errorMessage) => {
            setError('label', { type: 'server', message: errorMessage });
          });
          return;
        }
        if (err.message?.kind) {
          err.message.kind.forEach((errorMessage) => {
            setError('kind', { type: 'server', message: errorMessage });
          });
          return;
        }
        setError('root', { type: 'server', message: t('sidebar.errorEdit') });
      }
    }
  };

  const dropdownOptions = CUSTOM_FIELD_KINDS.map((kind) => ({
    id: kind.value,
    label: kind.label
  }));

  const handleAddOption = useCallback(() => {
    append({ label: '', value: '', isNew: true });
    clearErrors('list_options.root');
  }, [append, clearErrors]);

  const handleDeleteOption = useCallback((indexOption) => {
    remove(indexOption);
  }, [remove]);

  const selectedKind = watch('kind');

  // Ref para trackear si el usuario ha editado manualmente el value
  const valueEditedRef = React.useRef({});

  const handleLabelInput = useCallback((index, newLabel) => {
    // Si el usuario no ha editado manualmente el value, copiar el label
    if (!valueEditedRef.current[index]) {
      setValue(`list_options.${index}.value`, newLabel);
    }
  }, [setValue]);

  const handleValueInput = useCallback((index) => {
    // Marcar que el usuario editó manualmente este value
    valueEditedRef.current[index] = true;
  }, []);

  const renderListOption = (option, index) => (
    <div key={option.id} className="tw-flex tw-items-end tw-space-x-3 tw-mb-4">
      <InputMC
        id={`list_options.${index}.label`}
        name={`list_options.${index}.label`}
        label={t('sidebar.optionLabel')}
        disabled={!isCreateVariant && !option.isNew}
        className={`${
          !isCreateVariant && !option.isNew ? 'tw-opacity-85 tw-cursor-not-allowed' : ''
        }`}
        onInput={(e) => handleLabelInput(index, e.target.value)}
        useFloatingLabel
      />
      <InputMC
        id={`list_options.${index}.value`}
        name={`list_options.${index}.value`}
        label={t('sidebar.optionValue')}
        disabled={!isCreateVariant && !option.isNew}
        className={`${
          !isCreateVariant && !option.isNew ? 'tw-opacity-85 tw-cursor-not-allowed' : ''
        }`}
        onInput={() => handleValueInput(index)}
        useFloatingLabel
      />
      <ButtonMC
        text="×"
        onClick={() => handleDeleteOption(index)}
        variant="DANGER"
        outlined
        className="!tw-h-10 !tw-w-10 !tw-px-4 !tw-text-lg"
      />
    </div>
  );

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={title}
        submitAction={handleSubmit(onSubmit)}
        cancelAction={() => dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR })}
        submitLabel={t('sidebar.save')}
        cancelLabel={t('sidebar.cancel')}
      >
        <FormProviderMC
          methods={methods}
          className="tw-space-y-4"
        >
          {errors?.root && (
            <p className="tw-text-red-500 tw-text-sm">{errors.root.message}</p>
          )}

          {!isCreateVariant && (
            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-3 tw-mb-4">
              <p className="tw-text-yellow-700 tw-text-xs">
                {t('sidebar.warningNonEditable')}
              </p>
            </div>
          )}

          <InputMC
            id="label"
            name="label"
            label={t('sidebar.label')}
            className="tw-resize-none"
            useFloatingLabel
          />

          <InputMC
            id="name"
            name="name"
            label={t('sidebar.identifier')}
            className="tw-resize-none !tw-bg-gray-200 tw-text-gray-500 tw-cursor-not-allowed"
            disabled
            useFloatingLabel
          />

          <DropdownMC
            id="kind"
            name="kind"
            label={t('sidebar.kind')}
            options={dropdownOptions}
            defaultValue={selectedKind}
            disabled={!isCreateVariant}
            useFloatingLabel
          />

          <div className="tw-space-y-3 tw-pt-2">
            <Controller
              name="mandatory_in_mercately"
              control={control}
              render={({ field }) => (
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-500 tw-accent-blue-500 tw-rounded"
                  />
                  <span className="tw-text-sm tw-text-gray-700">{t('sidebar.mandatoryMercately')}</span>
                </label>
              )}
            />
            <Controller
              name="show_in_catalog"
              control={control}
              render={({ field }) => (
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-500 tw-accent-blue-500 tw-rounded"
                  />
                  <span className="tw-text-sm tw-text-gray-700">{t('sidebar.showInCatalog')}</span>
                </label>
              )}
            />
            <Controller
              name="mandatory_in_shops"
              control={control}
              render={({ field }) => (
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-500 tw-accent-blue-500 tw-rounded"
                  />
                  <span className="tw-text-sm tw-text-gray-700">{t('sidebar.mandatoryCatalog')}</span>
                </label>
              )}
            />
          </div>

          {selectedKind === 'list' && (
            <div className="tw-pt-4">
              <p className="tw-font-bold tw-mb-2">{t('sidebar.listOptions')}</p>
              {!isCreateVariant && (
                <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-3 tw-mb-4">
                  <p className="tw-text-yellow-700 tw-text-xs">
                    {t('sidebar.warningListOptions')}
                  </p>
                </div>
              )}
              {errors.list_options?.root && (
                <p className="tw-text-red-500 tw-text-sm tw-mb-2">{errors.list_options.root.message}</p>
              )}
              {fields.map((option, index) => renderListOption(option, index))}
              <ButtonMC
                text={t('sidebar.addOption')}
                onClick={handleAddOption}
                outlined
                className="tw-mt-2"
              />
            </div>
          )}
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default CreateEditCustomField;

